import { alpha, contrastColor } from '@mentimeter/ragnar-colors';
import { max as d3Max, mean } from 'd3-array';
import { axisBottom, axisLeft } from 'd3-axis';
import { easeElastic } from 'd3-ease';
import { scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { curveMonotoneX, line } from 'd3-shape';
import { transition } from 'd3-transition';
import type { ScalesT } from '@mentimeter/question-modules-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { calculateErrorMargins } from './calculateErrorMargins';
import { CorrectAnswer } from './CorrectAnswer';
import { displayValueToStep, stepToDisplayValue } from './transformValues';

const NUMBER_OF_STEPS = 20; // TODO: Update as new decision is being taken
const VISUALIZATION_AREA_GROUP_CLASS = 'visualization-area-group';
const GRAPH_AREA_CLASS = 'graph-area';
const VOTE_CIRCLE_GROUP_CLASS = 'vote-circle-group';
const VOTE_CIRCLE_CLASS = 'vote-circle';
const VOTE_CIRCLE_TEXT_CLASS = 'vote-circle-text';
const GRADIENT_ID = 'graph-gradient';

const d3 = {
  select,
  line,
  axisBottom,
  axisLeft,
  curveMonotoneX,
  mean,
  max: d3Max,
  easeElastic,
  transition,
};

type ScoreData = ScalesT['results'][0]['score'];

export function formatData(
  data: ScoreData | undefined,
): Array<[number, number]> {
  if (!data) return [];

  const scoresArray: Array<[number, number]> = Object.entries(data).map(
    ([key, value]) => [Number(key), value],
  );
  if (scoresArray.length === 0) return [];
  const firstScoreKey = scoresArray[0]![0];
  const lastScoreKey = scoresArray[scoresArray.length - 1]![0];
  return [[firstScoreKey, 0], ...scoresArray, [lastScoreKey, 0]];
}

export function getMaxScore(formattedData: Array<[number, number]>) {
  const scoreValues = formattedData.map((d) => d[1]);
  return d3.max(scoreValues) ?? 0;
}

export const NumericalScale = ({
  baseFontSize,
  data: _data,
  textColor,
  fills,
  lineColor,
  showCorrect,
  hasErrorMargin,
  displayMin,
  maxStep,
  errorMargin,
  correctAnswer,
  isScreenshotMode,
  scale,
}: {
  baseFontSize: number;
  data: ScoreData | undefined;
  textColor: string;
  fills: Array<string>;
  lineColor: string;
  showCorrect: boolean;
  hasErrorMargin: boolean;
  displayMin: number;
  maxStep: number;
  errorMargin: number;
  correctAnswer: number;
  isScreenshotMode: boolean;
  scale: number;
}) => {
  const [error, setError] = useState(false);
  const [isHovering, setHover] = useState(false);
  const [refHasRendered, setRefHasRendered] = useState(false);
  const [elementSize, setElementSize] = useState({
    width: 0,
    height: 0,
  });
  const [initialData] = useState(formatData(_data));
  const elementRef = useRef<HTMLDivElement | null>(null);

  const displayMax = stepToDisplayValue({
    scale,
    min: displayMin,
    step: maxStep,
  });
  const correctAnswerStep = displayValueToStep({
    displayValue: correctAnswer,
    min: displayMin,
    scale,
  });

  const formattedData = formatData(_data);

  const {
    lowerBoundary: displayedLowerBoundary,
    upperBoundary: displayedUpperBoundary,
  } = calculateErrorMargins({
    errorMargin,
    correctAnswer,
    min: displayMin,
    max: displayMax,
  });

  const lowerBoundaryStep = displayValueToStep({
    displayValue: displayedLowerBoundary,
    scale,
    min: displayMin,
  });
  const upperBoundaryStep = displayValueToStep({
    displayValue: displayedUpperBoundary,
    scale,
    min: displayMin,
  });

  const correctAnswerRange = useMemo(() => {
    const element = elementRef.current;
    if (!refHasRendered || !element) return;

    let lowerBound: number;
    let upperBound: number;
    const width = elementSize.width;
    const voteCircleRadius = calculateVoteCircleRadius(width);

    if (hasErrorMargin) {
      const lowerPosition = (width / maxStep) * lowerBoundaryStep;
      const upperPosition = (width / maxStep) * upperBoundaryStep;

      lowerBound =
        lowerBoundaryStep === correctAnswerStep
          ? ((lowerPosition - 2 * voteCircleRadius) /
              (width - 2 * voteCircleRadius)) *
            100
          : ((lowerPosition - voteCircleRadius) /
              (width - 2 * voteCircleRadius)) *
            100;
      upperBound =
        upperBoundaryStep === correctAnswerStep
          ? (upperPosition / (width - 2 * voteCircleRadius)) * 100
          : ((upperPosition - voteCircleRadius) /
              (width - 2 * voteCircleRadius)) *
            100;
    } else {
      const position = (width / maxStep) * correctAnswerStep;

      lowerBound =
        ((position - 2 * voteCircleRadius) / (width - 2 * voteCircleRadius)) *
        100;
      upperBound = (position / (width - 2 * voteCircleRadius)) * 100;
    }

    if (lowerBound < 0) {
      lowerBound = 0;
    }
    if (upperBound > 100) {
      upperBound = 100;
    }
    return { lower: lowerBound, upper: upperBound };
  }, [
    refHasRendered,
    elementSize,
    correctAnswerStep,
    hasErrorMargin,
    maxStep,
    lowerBoundaryStep,
    upperBoundaryStep,
  ]);

  const [visualizationColor, correctAnswerColor] = useMemo(() => {
    return [fills[0]!, fills[1]!];
  }, [fills]);

  // Draw graph
  useEffect(() => {
    const element = elementRef.current;
    if (!refHasRendered || !element) return;

    element.innerHTML = '';
    setError(false);

    try {
      const { width, height } = elementSize;
      const voteCircleRadius = calculateVoteCircleRadius(width);

      const svg = d3
        .select(element)
        .append('svg')
        .attr('overflow', 'visible')
        .attr('width', width)
        .attr('height', height)
        .attr('overflow', 'visible')
        .append('g')
        .attr('class', VISUALIZATION_AREA_GROUP_CLASS);

      //add the x Axis
      const xLine = scaleLinear()
        .range([voteCircleRadius, width - voteCircleRadius])
        .domain([0, maxStep]);
      svg.append('g').attr('opacity', 0).call(d3.axisBottom(xLine));

      //add the y Axis
      const yLine = scaleLinear()
        .range([height / 2, 0])
        .domain([0, Math.max(getMaxScore(initialData), 1)]);
      svg.append('g').attr('opacity', 0).call(d3.axisLeft(yLine));

      svg.append('defs').append('linearGradient').attr('id', GRADIENT_ID);
      // Plot the area
      svg
        .append('path')
        .datum(initialData)
        .style('fill', `url(#${GRADIENT_ID})`)
        .attr('class', GRAPH_AREA_CLASS)
        .attr('stroke-width', 1)
        .attr('stroke-linejoin', 'round')
        .attr(
          'd',
          d3
            .line()
            .curve(d3.curveMonotoneX)
            .x((d) => xLine(d[0]))
            .y((d) => yLine(d[1])),
        );

      svg
        .append('path')
        .attr(
          'd',
          `M${voteCircleRadius},${height / 2} L${width - voteCircleRadius},${height / 2}`,
        )
        .attr('stroke', lineColor)
        .attr('stroke-width', 4)
        .attr('opacity', 1);

      // Vote circles
      const voteCircles = svg
        .selectAll<SVGGElement, [number, number]>(`.${VOTE_CIRCLE_GROUP_CLASS}`)
        .data(initialData);
      voteCircles
        .enter()
        .append('g')
        .attr('class', VOTE_CIRCLE_GROUP_CLASS)
        .attr('opacity', 0)
        .attr('transform', (d) => `translate(${xLine(d[0])},${yLine(d[1])})`)
        .each(function () {
          const voteCircleGroup = d3.select<SVGGElement, [number, number]>(
            this,
          );
          voteCircleGroup
            .append('circle')
            .attr('class', VOTE_CIRCLE_CLASS)
            .attr('fill', visualizationColor)
            .attr('r', voteCircleRadius);
          voteCircleGroup
            .append('text')
            .attr('class', VOTE_CIRCLE_TEXT_CLASS)
            .attr('dy', '0.35em')
            .text((d) => d[1])
            .attr('font-size', `${voteCircleRadius}px`)
            .attr('text-anchor', 'middle')
            .attr('fill', contrastColor(visualizationColor));
        });

      const hoverArea = d3
        .select(element)
        .selectAll(`.${VISUALIZATION_AREA_GROUP_CLASS}`);
      hoverArea.on('mouseover', () => {
        setHover(true);
      });
      hoverArea.on('mouseleave', () => {
        setHover(false);
      });
    } catch (e) {
      setError(true);
    }
  }, [
    initialData,
    lineColor,
    textColor,
    baseFontSize,
    maxStep,
    correctAnswer,
    visualizationColor,
    refHasRendered,
    elementSize,
  ]);

  // Animation
  useEffect(() => {
    const element = elementRef.current;
    if (!refHasRendered || !element || !correctAnswerRange) return;

    const { width, height } = elementSize;

    const voteCircleRadius = calculateVoteCircleRadius(width);

    const graphArea = d3.select(element).selectAll(`.${GRAPH_AREA_CLASS}`);
    const xLine = scaleLinear()
      .domain([0, maxStep])
      .range([voteCircleRadius, width - voteCircleRadius]);
    const yLine = scaleLinear()
      .range([height / 2, 0])
      .domain([0, Math.max(getMaxScore(formattedData), 1)]);
    graphArea.datum(formattedData);
    graphArea
      .datum(formattedData)
      .transition()
      .duration(isScreenshotMode ? 0 : 1500)
      .ease(d3.easeElastic)
      .attr(
        'd',
        d3
          .line()
          .curve(d3.curveMonotoneX)
          .x((d) => xLine(d[0]))
          .y((d) => yLine(d[1])),
      );

    const gradient = d3.select(element).selectAll(`#${GRADIENT_ID}`);
    gradient.selectAll('stop').remove();
    const visualizationColorWithOpacity = alpha(visualizationColor, 0.5);
    const correctAnswerColorWithOpacity = alpha(correctAnswerColor, 0.75);

    // Create a gradient so that the plot has different colors depending on where the correct answer is
    if (showCorrect) {
      gradient
        .append('stop')
        .attr('offset', '0%')
        .attr('stop-color', visualizationColorWithOpacity);
      gradient
        .append('stop')
        .attr('offset', `${correctAnswerRange.lower}%`)
        .attr('stop-color', visualizationColorWithOpacity);
      gradient
        .append('stop')
        .attr('offset', `${correctAnswerRange.lower}%`)
        .attr('stop-color', correctAnswerColorWithOpacity);
      gradient
        .append('stop')
        .attr('offset', `${correctAnswerRange.upper}%`)
        .attr('stop-color', correctAnswerColorWithOpacity);
      gradient
        .append('stop')
        .attr('offset', `${correctAnswerRange.upper}%`)
        .attr('stop-color', visualizationColorWithOpacity);
      gradient
        .append('stop')
        .attr('offset', '100%')
        .attr('stop-color', visualizationColorWithOpacity);
    } else {
      gradient
        .append('stop')
        .attr('offset', '0%')
        .attr('stop-color', visualizationColorWithOpacity);
      gradient
        .append('stop')
        .attr('offset', '100%')
        .attr('stop-color', visualizationColorWithOpacity);
    }

    d3.select(element)
      .selectAll(`.${VOTE_CIRCLE_GROUP_CLASS}`)
      .data(formattedData)
      .attr('transform', (d) => `translate(${xLine(d[0])},${yLine(d[1])})`)
      .transition()
      .attr('opacity', (d) => (isHovering && d[1] > 0 ? '1' : '0'));
    const circles = d3
      .select(element)
      .selectAll(`.${VOTE_CIRCLE_CLASS}`)
      .data(formattedData);
    const circleText = d3
      .select(element)
      .selectAll(`.${VOTE_CIRCLE_TEXT_CLASS}`)
      .data(formattedData)
      .text((d) => d[1]);

    if (showCorrect) {
      if (hasErrorMargin) {
        circles.attr('fill', (d) =>
          d[0] >= lowerBoundaryStep && d[0] <= upperBoundaryStep
            ? correctAnswerColor
            : visualizationColor,
        );
        circleText.attr('fill', (d) =>
          d[0] >= lowerBoundaryStep && d[0] <= upperBoundaryStep
            ? contrastColor(correctAnswerColor)
            : contrastColor(visualizationColor),
        );
      } else {
        circles.attr('fill', (d) =>
          Number(d[0]) === Number(correctAnswer)
            ? correctAnswerColor
            : visualizationColor,
        );
        circleText.attr('fill', (d) =>
          Number(d[0]) === Number(correctAnswer)
            ? contrastColor(correctAnswerColor)
            : contrastColor(visualizationColor),
        );
      }
    } else {
      circles.attr('fill', visualizationColor);
      circleText.attr('fill', contrastColor(visualizationColor));
    }
  }, [
    refHasRendered,
    elementSize,
    formattedData,
    maxStep,
    correctAnswer,
    hasErrorMargin,
    lineColor,
    showCorrect,
    correctAnswerRange,
    visualizationColor,
    correctAnswerColor,
    isHovering,
    isScreenshotMode,
    lowerBoundaryStep,
    upperBoundaryStep,
  ]);

  // First render
  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    setElementSize({
      width: element.offsetWidth,
      height: element.offsetHeight,
    });
    setRefHasRendered(true);
  }, []);

  if (error) {
    return (
      <Box
        width="100%"
        height="100%"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize={2} color="textWeaker">
          Check your data.
        </Text>
      </Box>
    );
  }

  return (
    <>
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
        }}
        ref={elementRef}
      />
      <CorrectAnswer
        backgroundColor={lineColor}
        correctAnswer={correctAnswer}
        hasErrorMargin={hasErrorMargin}
        showCorrect={showCorrect}
        textColor={contrastColor(lineColor)}
        xPosition={getCorrectXPosition()}
        lowerBoundary={displayedLowerBoundary}
        upperBoundary={displayedUpperBoundary}
        limitTransitions={isScreenshotMode}
      ></CorrectAnswer>
    </>
  );

  function calculateVoteCircleRadius(width: number): number {
    return (width / NUMBER_OF_STEPS / 2) * 1.2;
  }

  function getCorrectXPosition(): number {
    const width = elementSize.width;
    const voteCircleRadius = calculateVoteCircleRadius(width);

    let position = (width / maxStep) * correctAnswerStep;

    if (position < voteCircleRadius) {
      position = voteCircleRadius;
    }
    if (position > width - voteCircleRadius) {
      position = width - voteCircleRadius;
    }

    return position;
  }
};
